import { validateTargetsData } from "helpers/validators/import-targets-validator";
import ExcelManager from "helpers/excel-manager";
import FileManager from "helpers/file-manager";
import React, { FC, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";
import { IStore } from "reducers";
import { getAllocationDimensions } from "reducers/selectors/collection";
import { getAllocationDimensionsByKind } from "components/product-collection";
import { formatMessage } from "lib/globalize-util";
import {
    IAllocationDimension,
    AllocationDimensionKind,
    getAllocationDimensionByIndex,
    AllocationDimensionIndex,
} from "helpers/allocation-dimension";
import { parseTargetsData } from "helpers/utils/targets";
import * as Sentry from "@sentry/browser"; // error tacking
import { ITargetsData, ITargetsDataError, RuleType } from "../../../types";
import { TargetsUploaderContext } from "./targets-uploader-context";
import { useTargetWizardContext } from "components/product-collection/footer/components/context/target-wizard-context";

interface ITargetsUploaderProviderProps {
    deliveries: IAllocationDimension;
    doors: IAllocationDimension;
}

export const BaseUploaderProvider: FC<ITargetsUploaderProviderProps> = ({ children, deliveries, doors }) => {
    const [errorMessages, setErrorMessages] = useState<ITargetsDataError>({});
    const [parsedTargetsData, setParsedTargetsData] = useState<ITargetsData[]>([]);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [wasProcessed, setWasProcessed] = useState<boolean>(false);
    const [hasDataToChange, setHasDataToChange] = useState<boolean>(false);
    const {
        targetGroupWithTargetsSnapshot: [targetGroupWithTargetsSnapshot],
    } = useTargetWizardContext();

    const handleErrorWhileProcessing = (errors: ITargetsDataError) => {
        setErrorMessages(errors);
    };

    const resetStates = useCallback(() => {
        setWasProcessed(false);
        setHasDataToChange(false);
        handleErrorWhileProcessing(undefined);
    }, []);

    const handleDrop = useCallback(
        async (file: File, onSettled: (error: boolean) => void) => {
            setIsUploading(true);
            resetStates();
            try {
                const result = await FileManager.readAsArrayBuffer(file);
                const targetsData = ExcelManager.read(result);
                const parsedData = parseTargetsData(targetsData, deliveries);
                const errors = validateTargetsData({
                    data: parsedData,
                    deliveries,
                    doors,
                    filters: targetGroupWithTargetsSnapshot.targets[0].filters,
                });
                const hasErrors = Object.keys(errors || {}).length > 0;

                onSettled(hasErrors);
                if (hasErrors) {
                    handleErrorWhileProcessing(errors);
                    return;
                }
                setParsedTargetsData(parsedData);
                setHasDataToChange(true);
            } catch (error) {
                handleErrorWhileProcessing({
                    [RuleType.GenericError]: formatMessage("Oops! Something went wrong. Please refresh and try again."),
                });
                Sentry.captureException(error);
                // eslint-disable-next-line no-console
                console.error(error);
            } finally {
                setIsUploading(false);
                setWasProcessed(true);
            }
        },

        [deliveries, doors, resetStates, targetGroupWithTargetsSnapshot]
    );

    const contextValue = useMemo(
        () => ({
            handleDrop,
            errorMessages,
            parsedTargetsData,
            isUploading,
            wasProcessed,
            hasDataToChange,
        }),
        [handleDrop, errorMessages, parsedTargetsData, isUploading, wasProcessed, hasDataToChange]
    );

    return <TargetsUploaderContext.Provider value={contextValue}>{children}</TargetsUploaderContext.Provider>;
};

const mapStateToProps = (state: IStore) => {
    const { collection } = state;
    const collectionId = collection.collectionId;

    const allocationDimensions = getAllocationDimensions(state, collectionId);

    const genericAllocationDimensions = getAllocationDimensionsByKind(
        allocationDimensions,
        AllocationDimensionKind.Generic
    );
    return {
        deliveries: getAllocationDimensionByIndex(genericAllocationDimensions, AllocationDimensionIndex.Date),
        doors: getAllocationDimensionByIndex(genericAllocationDimensions, AllocationDimensionIndex.Door),
    };
};

export const TargetsUploaderProvider = connect<ITargetsUploaderProviderProps, unknown, unknown>(mapStateToProps)(
    BaseUploaderProvider
);
